import { useCallback, useMemo, useState } from 'react';
import type { TelemetryVital } from '@/apis/telemetry';
import type { Extent } from './lib/extent';
import type { RangeView } from './lib/range';
import {
  canSelect,
  fitSelection,
  moveSelection,
  selectableExtent,
  selectionLabel,
  visibleOrigins,
  type Origin,
  type SelectionTarget,
} from './lib/selection';

/** Copy shown instead of the span readout when the trip is too short for handles (PRD §6). */
export const SHORT_TRIP_NOTE = 'Trip shorter than the minimum 6-hour window';

const ZOOM_IN_HINT = 'double-click to zoom';
const ZOOM_OUT_HINT = 'double-click to zoom out';

/** The handle selection in the full range, and what the charts draw for it. */
export interface RangeSelection {
  window: Extent;
  zoomed: boolean;
  selection: Extent;
  selectable: Extent;
  handles: boolean;
  origins: Origin[];
  note: string | null;
  move: (from: Extent, target: SelectionTarget, deltaMs: number) => void;
  setZoomed: (zoomed: boolean) => void;
}

/**
 * The window between the handles, which follows pickup and delivery (and an in-transit trip's growing end) until
 * a handle is moved. Outside the full range it's the operational window with no handles. Handles can't enter a
 * span that didn't load for any shown chart. Zoomed, the charts show only the selection, without handles or
 * origin lines, and zooming out finds the handles where they were.
 */
export const useRangeSelection = (
  view: RangeView,
  notLoaded: Partial<Record<TelemetryVital, Extent[]>>,
  timeZone: string
): RangeSelection => {
  const [picked, setPicked] = useState<Extent | null>(null);
  const [zoomRequested, setZoomed] = useState(false);
  const selectable = useMemo(
    () =>
      selectableExtent(
        view.window,
        view.operational,
        Object.values(notLoaded).flat()
      ),
    [view.window, view.operational, notLoaded]
  );
  const selecting = view.marked && canSelect(view.operational);
  const zoomed = selecting && zoomRequested;
  const selection = useMemo(
    () =>
      selecting && picked
        ? fitSelection(picked, selectable, view.operational)
        : view.operational,
    [selecting, picked, selectable, view.operational]
  );

  const move = useCallback(
    (from: Extent, target: SelectionTarget, deltaMs: number) => {
      const next = moveSelection(from, target, deltaMs, selectable, timeZone);
      setPicked((prev) => {
        const current = prev ?? view.operational;
        return current[0] === next[0] && current[1] === next[1] ? prev : next;
      });
    },
    [selectable, timeZone, view.operational]
  );

  return {
    window: zoomed ? selection : view.window,
    zoomed,
    selection,
    selectable,
    handles: selecting && !zoomed,
    origins:
      view.marked && !zoomed
        ? visibleOrigins(view.pickedUp, view.delivered, selection)
        : [],
    note: !view.marked
      ? null
      : selecting
        ? `${selectionLabel(selection, timeZone)} · ${zoomed ? ZOOM_OUT_HINT : ZOOM_IN_HINT}`
        : SHORT_TRIP_NOTE,
    move,
    setZoomed,
  };
};
